// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// One colour vocabulary for the whole site. Two rules govern everything here.
//
// 1. THE RAMP DARKENS AS THE AIR GETS WORSE. Lichen → ochre → ember → firebrick → dried blood.
//    The instinct on a light ground is a ramp running to bright yellow, which makes the worst
//    hour ever recorded the LIGHTEST mark on the page and inverts the message.
//
// 2. NOTHING FADES TOWARD THE BACKGROUND. On every scale here, fading toward the ground means
//    "clean", so a missing hour must never be able to read as safe air. Data-quality states
//    change the DRAWING — hollow rings, cross-hatch, stipple — never the hue.
//
// The band boundaries are the NSW Air Quality Category cut-offs, which are published values,
// not a quantile scale. A quantile ramp on this distribution (median hour about 6 µg/m³, record
// hour 13,516) spends four of its five colours below the Fair boundary and is useless.

import type { Pollutant } from './types';

export const BANDS = [
  { label: 'Good', hex: '#5B8C6E' },
  { label: 'Fair', hex: '#D9B04A' },
  { label: 'Poor', hex: '#D97A2B' },
  { label: 'Very poor', hex: '#B33A2B' },
  { label: 'Extremely poor', hex: '#6E1F3A' },
] as const;

/** Above the top of the published scale. Drawn, never dropped. */
export const ABOVE_SCALE = '#F2E3C4';

/** NSW AQC 1-hour cut-offs — the scale the map, the ledger and the matrix all draw against. */
export const HOUR_BOUNDS: Record<Pollutant, number[]> = {
  'pm2.5': [25, 50, 100, 300],
  pm10: [50, 100, 200, 600],
};

export function bandIndex(value: number | null, pollutant: Pollutant = 'pm2.5'): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const bounds = HOUR_BOUNDS[pollutant];
  for (let i = 0; i < bounds.length; i++) {
    if (value <= bounds[i]) return i;
  }
  return bounds.length;
}

export function bandColour(value: number | null, pollutant: Pollutant = 'pm2.5'): string {
  const index = bandIndex(value, pollutant);
  if (index === null) return 'var(--missing)';
  return BANDS[Math.min(index, BANDS.length - 1)].hex;
}

export function bandLabel(value: number | null, pollutant: Pollutant = 'pm2.5'): string {
  const index = bandIndex(value, pollutant);
  if (index === null) return 'no reading';
  return BANDS[Math.min(index, BANDS.length - 1)].label;
}

/** Wind lives outside the ramp entirely so it can never be misread as a concentration. */
export const WIND = '#7FA8C9';

/** The reader's own pinned station and hour. Nothing else uses this colour. */
export const ACCENT = '#7C5CFF';

export const JURISDICTION_COLOUR: Record<string, string> = {
  NSW: '#7FA8C9',
  QLD: '#C98F5B',
  ACT: '#9C8FC9',
};

export const EPISODE_TYPE_COLOUR: Record<string, string> = {
  Smoke: '#B33A2B',
  Dust: '#C98F5B',
  'Winter inversion': '#6E7FA8',
  Unclassified: '#7A736C',
};

/**
 * The traffic-light colours the three jurisdictions publish. Used in exactly one view — Three
 * Labels — and nowhere else, so they can never be confused with this site's own ramp.
 */
export const OFFICIAL_BAND_COLOUR = ['#42A93C', '#EEC900', '#E47400', '#BA0029', '#590019'];

/**
 * A WITHIN-STATION scale, for the question "which bearing is dirty *here*".
 *
 * Australia's air is clean almost everywhere almost always, so at a coastal city every wedge of
 * a pollution rose falls in the published "Good" band and the chart renders as a single flat
 * colour — true, and useless. This is a second, explicitly different encoding: how many times
 * the station's own median hour a bin runs. The breaks are FIXED ratios, not quantiles, so a
 * clean station and a dirty one are still drawn on the same scale and can be compared. It is
 * given its own hue family so it can never be mistaken for the absolute band ramp, and its
 * legend always says what the multiple is of.
 */
export const RATIO_BREAKS = [1, 1.5, 2, 3];
export const RATIO_COLOUR = ['#2F4858', '#41697A', '#5E8CA0', '#93B7C4', '#D7E4E8'];
export const RATIO_LABELS = [
  'at or below the median hour',
  '1–1.5× the median hour',
  '1.5–2×',
  '2–3×',
  'more than 3×',
];

export function ratioColour(value: number | null, median: number | null): string {
  if (value === null || median === null || !Number.isFinite(value) || !median) return 'var(--missing)';
  const ratio = value / median;
  for (let i = 0; i < RATIO_BREAKS.length; i++) {
    if (ratio <= RATIO_BREAKS[i]) return RATIO_COLOUR[i];
  }
  return RATIO_COLOUR[RATIO_COLOUR.length - 1];
}
